import type { Spec } from "@json-render/core";
import { isNonEmptySpec, validateSpec } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import { z } from "zod";

/**
 * The internal-UI json-render catalog.
 *
 * Unlike the email/PDF renderers (which render server-side to HTML/PDF bytes),
 * internal UI specs are rendered as live React in the browser using our own
 * `@erp/ui` design system — see `@erp/json-renderer/react` for the renderer and
 * the component registry. This module is deliberately server-safe: it only
 * declares the catalog (component names + Zod prop schemas) so the backend can
 * validate specs and AI tooling can generate them, without pulling `@erp/ui`
 * (browser-only) into a Node bundle.
 *
 * Component definitions intentionally mirror the design vocabulary in DESIGN.md:
 * warm-stone neutrals, semantic status colors only, 14px-dense body, sentence
 * case. Props stay small and declarative so they are easy for both humans and
 * models to author.
 */

const spacingScale = z
  .enum(["none", "sm", "md", "lg", "xl"])
  .describe(
    "Spacing step from the design scale (sm=8px, md=12px, lg=16px, xl=24px).",
  );

const alignment = z.enum(["start", "center", "end", "stretch"]);
const justification = z.enum(["start", "center", "end", "between", "around"]);

const textTone = z
  .enum(["default", "muted", "destructive", "success", "warning"])
  .describe(
    "Semantic tone. Color is only ever used to signal status, never decoration.",
  );

const internalUiSchema = schema;

const INTERNAL_UI_COMPONENTS = {
  Box: {
    props: z.object({
      direction: z.enum(["row", "col"]).optional(),
      gap: spacingScale.optional(),
      padding: spacingScale.optional(),
      align: alignment.optional(),
      justify: justification.optional(),
      wrap: z.boolean().optional(),
    }),
    slots: ["default"],
    description:
      "Generic flex container; children keep their NATURAL width. Use `direction: row` for tight inline items (badges, chips, a label + value). For cards/charts that should fill the width, use Row or Grid instead.",
  },
  Stack: {
    props: z.object({
      gap: spacingScale.optional(),
      padding: spacingScale.optional(),
      align: alignment.optional(),
      justify: justification.optional(),
    }),
    slots: ["default"],
    description: "Vertical stack of children with consistent spacing.",
  },
  Row: {
    props: z.object({
      gap: spacingScale.optional(),
      padding: spacingScale.optional(),
      align: alignment.optional(),
      justify: justification.optional(),
      wrap: z.boolean().optional(),
    }),
    slots: ["default"],
    description:
      "Horizontal row whose children STRETCH to share the full width equally — use for cards or charts side by side. For natural-width inline items (badges, a label + value) use Box with direction: row instead.",
  },
  Grid: {
    props: z.object({
      columns: z
        .union([z.literal(2), z.literal(3), z.literal(4)])
        .optional()
        .describe("Number of equal-width columns (default 2)."),
      gap: spacingScale.optional(),
    }),
    slots: ["default"],
    description:
      "Equal-width responsive grid. Use this to place cards or charts side by side — each cell gets real width so charts render. Collapses to one column on narrow screens.",
  },
  Heading: {
    props: z.object({
      value: z.string(),
      level: z
        .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
        .optional()
        .describe("1=display, 2=headline, 3=title, 4=subtitle."),
    }),
    slots: [],
    description: "A section heading. Sentence case, never Title Case.",
  },
  Text: {
    props: z.object({
      value: z.string(),
      variant: z
        .enum(["body", "label", "muted", "eyebrow", "subtitle"])
        .optional(),
      tone: textTone.optional(),
      align: z.enum(["left", "center", "right"]).optional(),
    }),
    slots: [],
    description:
      "A run of text. `eyebrow` is the only uppercase style in the system.",
  },
  Card: {
    props: z.object({
      title: z.string().optional(),
      description: z.string().optional(),
    }),
    slots: ["default"],
    description:
      "A white card with a 1px warm-border. Optional title/description header; children render as the body.",
  },
  Button: {
    props: z.object({
      label: z.string(),
      variant: z
        .enum([
          "default",
          "destructive",
          "outline",
          "secondary",
          "ghost",
          "link",
        ])
        .optional(),
      size: z.enum(["default", "sm", "lg"]).optional(),
      disabled: z.boolean().optional(),
    }),
    slots: [],
    description:
      "An action button. Emits a `press` event when clicked. Use `default` for the primary action only.",
  },
  Badge: {
    props: z.object({
      label: z.string(),
      variant: z
        .enum([
          "default",
          "secondary",
          "destructive",
          "outline",
          "success",
          "warning",
        ])
        .optional(),
    }),
    slots: [],
    description:
      "A pill badge for status. Use semantic variants to signal state.",
  },
  Input: {
    props: z.object({
      value: z.string().optional(),
      placeholder: z.string().optional(),
      type: z.enum(["text", "email", "number", "password"]).optional(),
      disabled: z.boolean().optional(),
    }),
    slots: [],
    description:
      'A text input. Bind `value` with `{ $bindState: "/path" }` for two-way binding.',
  },
  Alert: {
    props: z.object({
      title: z.string().optional(),
      description: z.string().optional(),
      variant: z.enum(["default", "destructive"]).optional(),
    }),
    slots: [],
    description: "An inline callout for important context.",
  },
  Separator: {
    props: z.object({
      orientation: z.enum(["horizontal", "vertical"]).optional(),
    }),
    slots: [],
    description: "A 1px divider in the warm-border color.",
  },
  Table: {
    props: z.object({
      columns: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          align: z.enum(["left", "right", "center"]).optional(),
        }),
      ),
      rows: z
        .array(z.record(z.string(), z.unknown()))
        .describe(
          'Row objects keyed by column `key`. Often bound with `{ $state: "/path" }`.',
        ),
    }),
    slots: [],
    description:
      'A dense data table. Numeric columns should use align: "right" for tabular alignment.',
  },
  Chart: {
    props: z.object({
      spec: z
        .record(z.string(), z.unknown())
        .describe("An inline-data Vega-Lite spec, rendered with @erp/viz."),
      title: z.string().optional(),
      height: z.number().optional(),
    }),
    slots: [],
    description:
      "A chart. `spec` is a Vega-Lite spec with inline data (no remote URLs).",
  },
  Sheet: {
    props: z.object({
      title: z.string().optional(),
      description: z.string().optional(),
      open: z
        .boolean()
        .optional()
        .describe(
          'Whether the sheet is shown. Bind two-way with { $bindState: "/path" } so an action can open it and the close control dismisses it.',
        ),
      side: z
        .enum(["left", "right", "top", "bottom"])
        .optional()
        .describe("Edge the sheet slides in from (default right)."),
      size: z
        .number()
        .optional()
        .describe(
          "Initial panel size as a % of the viewport (left/right sides).",
        ),
    }),
    slots: ["default"],
    description:
      'A slide-in panel (drawer). Children render as the body. Bind `open` to state with { $bindState: "/path" } and open it from a Button\'s on.press action that sets that state — e.g. a row that opens its own detail. Resizable by dragging its edge.',
  },
};

/** Action `params` schemas keyed by action name, for a dynamic catalog. */
export type InternalUiActionDefs = Record<
  string,
  { params: z.ZodType; description?: string }
>;

/**
 * Build an internal-UI catalog, optionally with dynamic actions. Actions are
 * how a spec binds a control (e.g. a Button's `on.press`) to behavior: the
 * `params` Zod schema — often produced from a Schema Definition via the
 * backend's `generateZodSchema` — types what the control submits. At render
 * time the host supplies a matching `handlers` map to `InternalUiRenderer`.
 */
export function buildInternalUiCatalog(actions: InternalUiActionDefs = {}) {
  const actionEntries = Object.fromEntries(
    Object.entries(actions).map(([name, def]) => [
      name,
      { params: def.params, description: def.description ?? name },
    ]),
  );
  return schema.createCatalog({
    components: INTERNAL_UI_COMPONENTS,
    actions: actionEntries,
  });
}

export const internalUiCatalog = buildInternalUiCatalog();

export { internalUiSchema };

/** A json-render spec targeting the internal-UI catalog. */
export type InternalUiSpec = Spec;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The catalog's Zod schema requires an explicit `visible` on every element,
 * but the renderer treats a missing `visible` as "always visible". Default it
 * here so authored/AI-generated specs don't need the boilerplate. Returns a
 * normalized copy; the input is never mutated.
 */
function withDefaultVisibility(spec: unknown): unknown {
  if (!isRecord(spec) || !isRecord(spec.elements)) return spec;
  const elements: Record<string, unknown> = {};
  for (const [id, element] of Object.entries(spec.elements)) {
    elements[id] =
      isRecord(element) && !("visible" in element)
        ? { ...element, visible: true }
        : element;
  }
  return { ...spec, elements };
}

export interface InternalUiValidationResult {
  valid: boolean;
  /** Human-readable error messages, prefixed with the offending element key. */
  issues: string[];
}

/**
 * Validate an internal-UI spec. Combines two checks:
 *
 * 1. Structural integrity (`validateSpec`) — missing root, dangling child
 *    references, `visible`/`on` misplaced inside props, etc.
 * 2. Catalog membership — every element's `type` must be a known internal-UI
 *    component.
 *
 * Per-prop type/required checks are intentionally NOT enforced: json-render
 * keeps props loose so partial/streaming specs validate, and the renderer
 * coerces props defensively. Validate guards structure and component names.
 */
export function validateInternalUiSpec(
  spec: unknown,
  actions: InternalUiActionDefs = {},
): InternalUiValidationResult {
  if (!isNonEmptySpec(spec)) {
    return {
      valid: false,
      issues: ["Spec is empty or missing a root/elements map."],
    };
  }

  const issues: string[] = [];

  for (const issue of validateSpec(spec).issues) {
    if (issue.severity !== "error") continue;
    issues.push(
      issue.elementKey
        ? `${issue.elementKey}: ${issue.message}`
        : issue.message,
    );
  }

  // Use the base catalog unless the spec binds actions, in which case validate
  // against a catalog that knows those action names + param schemas.
  const catalog =
    Object.keys(actions).length > 0
      ? buildInternalUiCatalog(actions)
      : internalUiCatalog;
  const catalogResult = catalog.validate(withDefaultVisibility(spec));
  if (!catalogResult.success && catalogResult.error) {
    for (const issue of catalogResult.error.issues) {
      issues.push(`${issue.path.join("/") || "(root)"}: ${issue.message}`);
    }
  }

  return { valid: issues.length === 0, issues };
}
